<template>
  <ContentWrap>
    <el-alert type="info" :closable="false" show-icon class="mb-12px" title="本所退费：草稿 → 提交 → 审批 → 确认退款。不自动冲减收款台账。" />
    <el-form :inline="true" class="mb-12px" @submit.prevent>
      <el-form-item label="案件ID">
        <el-input-number v-model="query.caseId" :min="1" controls-position="right" class="!w-120px" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="query.status" clearable class="!w-130px">
          <el-option label="草稿" value="DRAFT" />
          <el-option label="待审" value="PENDING" />
          <el-option label="已通过" value="APPROVED" />
          <el-option label="已驳回" value="REJECTED" />
          <el-option label="已退款" value="REFUNDED" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery">搜索</el-button>
        <el-button type="success" @click="openCreate">新建退费</el-button>
      </el-form-item>
    </el-form>
    <el-table v-loading="loading" :data="list" border>
      <el-table-column prop="id" label="编号" width="70" />
      <el-table-column prop="firmCaseNo" label="案号" min-width="110" />
      <el-table-column prop="amount" label="金额" width="100" />
      <el-table-column prop="reason" label="原因" min-width="140" show-overflow-tooltip />
      <el-table-column prop="status" label="状态" width="100" />
      <el-table-column label="操作" width="200" fixed="right">
        <template #default="{ row }">
          <el-button v-if="row.status === 'DRAFT'" link type="primary" @click="doSubmit(row)">提交</el-button>
          <el-button v-if="row.status === 'PENDING'" link type="success" @click="doApprove(row, true)">通过</el-button>
          <el-button v-if="row.status === 'PENDING'" link type="danger" @click="doApprove(row, false)">驳回</el-button>
          <el-button v-if="row.status === 'APPROVED'" link type="warning" @click="doConfirm(row)">确认退款</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination v-model:page="query.pageNo" v-model:limit="query.pageSize" :total="total" @pagination="getList" />
    <Dialog v-model="visible" title="新建退费" width="480px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="90px">
        <el-form-item label="案件" prop="caseId">
          <el-select v-model="form.caseId" filterable class="!w-100%">
            <el-option v-for="m in matters" :key="m.id" :label="`${m.firmCaseNo || m.id} · ${m.title}`" :value="m.id!" />
          </el-select>
        </el-form-item>
        <el-form-item label="金额" prop="amount"><el-input-number v-model="form.amount" :min="0.01" :precision="2" class="!w-100%" /></el-form-item>
        <el-form-item label="原因" prop="reason"><el-input v-model="form.reason" type="textarea" :rows="2" /></el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="visible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submit">保存草稿</el-button>
      </template>
    </Dialog>
  </ContentWrap>
</template>
<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import { FirmOfficeApi, type PracticeRefundVO } from '@/api/firm/office'
import { FirmPracticeApi, type PracticeMatterVO } from '@/api/firm/practice'
defineOptions({ name: 'FirmPracticeRefund' })
const message = useMessage()
const loading = ref(false)
const list = ref<PracticeRefundVO[]>([])
const total = ref(0)
const query = reactive({ pageNo: 1, pageSize: 10, caseId: undefined as number | undefined, status: undefined as string | undefined })
const matters = ref<PracticeMatterVO[]>([])
const visible = ref(false)
const saving = ref(false)
const formRef = ref<FormInstance>()
const form = reactive({ caseId: undefined as number | undefined, amount: 0, reason: '' })
const rules: FormRules = {
  caseId: [{ required: true, message: '请选择案件', trigger: 'change' }],
  amount: [{ required: true, message: '请输入金额', trigger: 'blur' }],
  reason: [{ required: true, message: '请填写原因', trigger: 'blur' }]
}
const getList = async () => {
  loading.value = true
  try {
    const data = await FirmOfficeApi.refundPage(query)
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}
const handleQuery = () => { query.pageNo = 1; getList() }
const openCreate = async () => {
  Object.assign(form, { caseId: undefined, amount: 0, reason: '' })
  visible.value = true
  const m = await FirmPracticeApi.matterPage({ pageNo: 1, pageSize: 100 })
  matters.value = m?.list || []
}
const submit = async () => {
  await formRef.value?.validate()
  if (!form.caseId) return
  saving.value = true
  try {
    await FirmOfficeApi.refundCreate({ caseId: form.caseId, amount: form.amount, reason: form.reason.trim() })
    message.success('已创建')
    visible.value = false
    await getList()
  } finally {
    saving.value = false
  }
}
const doSubmit = async (row: PracticeRefundVO) => {
  if (!row.id) return
  await FirmOfficeApi.refundSubmit(row.id)
  message.success('已提交')
  await getList()
}
const doApprove = async (row: PracticeRefundVO, approved: boolean) => {
  if (!row.id) return
  const comment = approved ? undefined : window.prompt('驳回意见') || undefined
  if (!approved && !comment) return
  await FirmOfficeApi.refundApprove({ id: row.id, approved, comment })
  message.success(approved ? '已通过' : '已驳回')
  await getList()
}
const doConfirm = async (row: PracticeRefundVO) => {
  if (!row.id) return
  await FirmOfficeApi.refundConfirm(row.id)
  message.success('已确认退款')
  await getList()
}
onMounted(getList)
</script>
